import { css, keyframes } from '@emotion/react';
import { theme } from '../../../design-system/theme/theme';
import type { RunState } from '../../types';

export const titleCaptionStyle = css`
  font-size: ${theme.fontSize.sm}px;
  font-weight: 400;
  color: ${theme.color.textMuted};
`;

/* 툴팁 기준 컨테이너 — 셀들이 이 안에서 절대배치 툴팁의 기준(relative)이 된다. */
export const heatWrapStyle = css`
  position: relative;
`;

/** 툴팁 등장 — 오른쪽으로 살짝 밀려나며 페이드 인. */
const tipIn = keyframes`
  from { opacity: 0; transform: translate(8px, -50%); }
  to { opacity: 1; transform: translate(14px, -50%); }
`;

/** 커스텀 hover 툴팁 — 기본 title(지연·스타일 불가) 대체. 커서 오른쪽에 즉시 표시. */
export const tooltipStyle = css`
  position: absolute;
  z-index: 20;
  transform: translate(14px, -50%);
  padding: 7px 11px;
  background: ${theme.color.text};
  color: ${theme.color.onPrimary};
  border-radius: ${theme.radius.sm}px;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  pointer-events: none;
  animation: ${tipIn} 0.12s ease-out;

  /* 왼쪽 꼬리(삼각형) — 커서 쪽을 가리킨다. */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: ${theme.color.text};
  }
`;

/** 툴팁 제목 = 날짜/시각. 구분선으로 상태와 분리. */
export const tooltipTitleStyle = css`
  display: block;
  font-weight: 700;
  font-size: 13px;
  padding-bottom: 5px;
  margin-bottom: 5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
`;

/** 상태 행 = 색 점 + 상태 라벨. */
export const tooltipRowStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
`;

/** 상태 색 점 — 범례와 연결. */
export const tooltipDotStyle = (color: string) => css`
  width: 8px;
  height: 8px;
  border-radius: ${theme.radius.pill}px;
  background: ${color};
  border: ${color === theme.color.surface ? `1px solid ${theme.color.border}` : 'none'};
  flex-shrink: 0;
`;

/** 하단 클릭 안내(클릭 가능한 셀에만). */
export const tooltipHintStyle = css`
  display: block;
  margin-top: 5px;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.65;
`;

export const rowStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.space.md}px;
  padding: ${theme.space.xs}px 0;
`;

/* 모듈명 열 7rem — 히트맵을 고정 폭으로 좁게 둘 때 최소 폭. 넘치면 말줄임. */
export const nameStyle = (bad?: boolean) => css`
  flex: 0 0 7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${theme.fontSize.md}px;
  color: ${bad ? theme.color.danger : theme.color.text};
  font-weight: ${bad ? 500 : 400};
`;

/* 모듈명 클릭 가능 — 수집 이력(필터 적용)으로 이동. hover 시 primary 색 + 밑줄로 링크임을 표시. */
export const nameClickableStyle = css`
  cursor: pointer;
  &:hover {
    color: ${theme.color.primary};
    text-decoration: underline;
  }
`;

export const cellsStyle = css`
  display: flex;
  gap: ${theme.space.xs}px;
`;

/* csv parser 그룹 — 앞 그룹(비 csv)과 살짝 여백을 두고 아래에 나열. */
export const csvGroupStyle = css`
  margin-top: ${theme.space.md}px;
`;

export const stateColor: Record<RunState, string> = {
  S: theme.color.success,
  // 재시도 성공 — success와 같은 초록 계열의 밝은 톤(success2)으로 구분.
  Y: theme.color.success2,
  F: theme.color.danger,
  R: theme.color.running,
  U: theme.color.surface,
  _: theme.color.border,
};

/* 셀 16×16 고정. */
export const cellStyle = (state: RunState, clickable: boolean) => css`
  width: 16px;
  height: 16px;
  border-radius: ${theme.radius.sm}px;
  background: ${stateColor[state] ?? theme.color.border};
  ${clickable &&
  /* hover outline은 고정색이 아니라 셀 상태 배경색과 일관되게 (같은 색 반투명). */
  `cursor: pointer;
   &:hover { outline: 2px solid ${stateColor[state] ?? theme.color.border}66; }`}
`;

export const hintStyle = css`
  margin: ${theme.space.md}px 0 0;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};
`;
